import {
  CustomImage,
  CustomModal,
  CustomPagination,
  CustomTable,
  Text,
  useText,
} from '@src/components';
import modalClose from '@src/assets/icons/icon-close.svg';
import '@src/styles/myOrders/orderSummaryCardModal.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { moneyFormat } from '@src/utils/utils';
import iconChecked from '@src/assets/icons/icon-checked.svg';
import iconNoChecked from '@src/assets/icons/icon-no-check.svg';
import cardIcon from '@src/assets/icons/icon-kart.svg';
import { useDispatch, useSelector } from 'react-redux';
import { orderDetailsGetPageList } from '@src/store/slice/orderSlice';
import { monthsList, VouIdCode } from '@src/store/enums';
import { customerLogosGetList } from '@src/store/slice/customerSlice';
import { cardOrderDetail } from '@src/store/slice/cardOrderDetailtsSlice';
import PropTypes from 'prop-types';
import iconNpInfo from '@src/assets/icons/icon-info.svg';

const OrderSummaryCardModal = ({
  modalVisible,
  handleModalVisible,
  isPending = false,
  fromPage = false,
  isCompliments = false,
  isMarket = false,
}) => {
  const dispatch = useDispatch();
  const [companyLogos, setCompanyLogos] = useState([]);
  const [cardTypeFizikselText, setCardTypeFizikselText] = useState(false);

  const { selectedOrder } = useSelector((state) => state?.order);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  let vouId;
  if (isMarket) {
    vouId = VouIdCode.Market;
  } else if (isCompliments) {
    vouId = VouIdCode.Compliments;
  } else {
    vouId = VouIdCode.Restaurant;
  }

  const refNoText = useText('refNo');
  const cardText = useText('card');
  const printNameSurnameText = useText('printNameSurname');
  const printWithoutNameText = useText('printWithoutName');
  const dontPrintNameSurnameText = useText('dontPrintNameSurname');
  const printLogoText = useText('printLogo');
  const dontPrintLogoText = useText('dontPrintLogo');
  const printCompanyNameText = useText('printCompanyName');
  const dontPrintCompanyNameText = useText('dontPrintCompanyName');

  const invoiceMonthText = monthsList
    .filter((month) => month.value === selectedOrder?.invoiceMonth)
    .reduce((acc, month) => month.text, '');

  const [data, setData] = React.useState({
    list: [],
    tableProperty: {
      currentPage: 1,
      page: 1,
      pageSize: 10,
      totalCount: 0,
    },
  });

  const getBodyForPageList = () => {
    let body = {
      vouId: VouIdCode.Restaurant,
      orderId: fromPage == 'deliveryConfirmations' ? selectedOrder?.orderId : selectedOrder?.id,
    };
    if (isCompliments) {
      //body.ShowOnlyPriced = true;
      body.vouId = VouIdCode.Compliments;
    }
    if (isMarket) {
      body.vouId = VouIdCode.Market;
    }
    return body;
  };

  const getPageList = useCallback(
    async (pageNumber, pageSize) => {
      const action = await dispatch(
        orderDetailsGetPageList({
          body: getBodyForPageList(),
          pageNumber,
          pageSize,
        }),
      );
      if (orderDetailsGetPageList.fulfilled.match(action)) {
        setData({
          list: action?.payload?.data?.items,
          tableProperty: action?.payload?.data?.pagedProperty,
        });
      }
    },
    [dispatch, selectedOrder?.id, isCompliments, isMarket],
  );

  const getBodyForPendingList = () => {
    let body = {
      vouId: VouIdCode.Restaurant,
      cardOrderId: selectedOrder?.id,
      customerId: selectedCustomer?.customerId,
    };
    if (isCompliments) {
      body.ShowOnlyPriced = true;
      body.vouId = VouIdCode.Compliments;
    } else if (isMarket) {
      body.ShowOnlyPriced = true;
      body.vouId = VouIdCode.Market;
    }
    return body;
  };

  const pendingApprovedList = useCallback(
    async (pageNumber, pageSize) => {
      const action = await dispatch(
        cardOrderDetail({
          body: getBodyForPendingList(),
          pageNumber,
          pageSize,
        }),
      );
      if (cardOrderDetail.fulfilled.match(action)) {
        setData({
          list: action?.payload?.data?.items,
          tableProperty: action?.payload?.data?.pagedProperty,
        });
      }
    },
    [dispatch, selectedOrder?.id, selectedCustomer?.customerId, isCompliments, isMarket],
  );

  useEffect(() => {
    if (modalVisible) {
      if (isPending) {
        (async () => {
          await pendingApprovedList(1, 10);
          const action = await dispatch(customerLogosGetList({ vouId: vouId }));
          if (customerLogosGetList.fulfilled.match(action)) {
            setCompanyLogos(action?.payload?.data);
          }
        })();
      } else {
        (async () => {
          await getPageList(1, 10);
          const action = await dispatch(customerLogosGetList({ vouId: vouId }));
          if (customerLogosGetList.fulfilled.match(action)) {
            setCompanyLogos(action?.payload?.data);
          }
        })();
      }
    }
  }, [dispatch, getPageList, modalVisible, pendingApprovedList]);

  useEffect(() => {
    if (selectedOrder.cardTypeId === false || selectedOrder.cardType === 0) {
      setCardTypeFizikselText(true);
    }
  }, [selectedOrder.cardTypeId]);

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      if (isPending) {
        await getPageList(pageNumber, pageSize);
      } else {
        await pendingApprovedList(pageNumber, pageSize);
      }
    },
    [getPageList],
  );

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      handleModalVisible(false);
    },
    [handleModalVisible],
  );

  const columns = (isCompliments || isMarket)
    ? [
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        align: 'center',
        width: 50,
        render: (_, __, index) =>
          (data?.tableProperty?.currentPage - 1) * data?.tableProperty?.pageSize + index + 1,
      },
      {
        title: <Text t="nameUpperCase" />,
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        key: 'name',
        width: (isCompliments || isMarket) ? 150 : 162.7,
      },
      {
        title: <Text t="surnameUpperCase" />,
        dataIndex: 'surname',
        key: 'surname',
        width: (isCompliments || isMarket) ? 150 : 162.7,
      },
      {
        title: (
          <span>
            <Text t={refNoText} /> <span className="red-color">*</span>
            <CustomImage
              style={{ marginLeft: 3 }}
              className="order-expand-button-img"
              src={iconNpInfo}
            />
          </span>
        ),
        dataIndex: 'refNo',
        key: 'refNo',
        width: 140,
        align: 'center',
      },
      {
        title: <Text t="gsmNoUpperCase" />,
        dataIndex: 'gsm',
        key: 'gsm',
        width: 150,
        align: 'center',
      },
      {
        title: (
          <span>
            <Text t="deliveryAddress" /> <span className="red-color">*</span>{' '}
          </span>
        ),
        width: 162.7,
        dataIndex: 'addressText',
        key: 'addressText',
        align: 'center',
      },
      {
        title: (
          <span>
            <Text t="mobilePayment" />
            <CustomImage
              style={{ marginLeft: 3 }}
              className="order-expand-button-img"
              src={iconNpInfo}
            />
          </span>
        ),
        dataIndex: 'mobilePaymentAuthorization',
        key: 'mobilePaymentAuthorization',
        width: 130,
        align: 'center',
        render: (text) =>
          text ? (
            <CustomImage src={iconChecked} alt="check" />
          ) : (
            <CustomImage src={iconNoChecked} alt="uncheck" />
          ),
      },
      {
        title: <Text t="totalPrice" />,
        dataIndex: 'price',
        key: 'price',
        width: (isCompliments || isMarket) ? 140 : 110,
        align: 'center',
        render: (text) => <span>{text ? `${moneyFormat(text, false)}` : '0'}</span>,
      },
    ]
    : [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        fixed: 'left',
        align: 'center',
        width: 50,
        render: (_, __, index) =>
          (data?.tableProperty?.currentPage - 1) * data?.tableProperty?.pageSize + index + 1,
      },
      {
        title: <Text t="nameUpperCase" />,
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        key: 'name',
        width: 162.7,
      },
      {
        title: <Text t="surnameUpperCase" />,
        dataIndex: 'surname',
        key: 'surname',
        width: 162.7,
      },
      {
        title: (
          <span>
            <Text t={refNoText} /> <span className="red-color">*</span>
          </span>
        ),
        dataIndex: 'refNo',
        key: 'refNo',
        width: 140,
        align: 'center',
      },
      {
        title: <Text t="gsmNoUpperCase" />,
        dataIndex: 'gsm',
        key: 'gsm',
        width: 150,
        align: 'center',
      },
      {
        title: (
          <span>
            <Text t="deliveryAddress" /> <span className="red-color">*</span>{' '}
          </span>
        ),
        width: 162.7,
        dataIndex: 'addressText',
        key: 'addressText',
        align: 'center',
      },
      {
        title: (
          <span>
            <Text t="mobilePayment" />
          </span>
        ),
        dataIndex: 'mobilePaymentAuthorization',
        key: 'mobilePaymentAuthorization',
        width: 110,
        align: 'center',
        render: (text) =>
          text ? (
            <CustomImage src={iconChecked} alt="check" />
          ) : (
            <CustomImage src={iconNoChecked} alt="uncheck" />
          ),
      },
      {
        title: <Text t="totalPrice" />,
        dataIndex: 'price',
        key: 'price',
        width: 110,
        align: 'center',
        render: (text) => <span>{text ? `${moneyFormat(text, false)}` : '0'}</span>,
      },
    ];

  const cardParemetersText = () => {
    const text = [];
    if (selectedOrder?.printNameSurname) {
      text.push(printNameSurnameText);
    } else {
      text.push((isCompliments || isMarket) ? printWithoutNameText : dontPrintNameSurnameText);
    }

    if (companyLogos.length > 0) {
      if (selectedOrder?.printCompanyLogo) {
        text.push(printLogoText);
      } else {
        text.push(dontPrintLogoText);
      }
    }

    if (selectedOrder?.printCompanyName) {
      text.push(printCompanyNameText);
    } else {
      text.push(dontPrintCompanyNameText);
    }
    return text.join(' / ');
  };

  return (
    <CustomModal
      className={`${(isCompliments || isMarket)
        ? 'order-summary-card-modal gift-order-summary-card-modal'
        : 'order-summary-card-modal'
        }`}
      width={1116}
      maskClosable={false}
      footer={false}
      title={useText((isCompliments || isMarket) ? 'orderDetail' : 'yourOrderSummary')}
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <div className="summary-info-container">
        <div
          className="summary-info-content"
          style={{ justifyContent: (isCompliments || isMarket) ? 'center' : 'flex-end' }}
        >
          {(isCompliments || isMarket) ? null : (
            <div className="summary-info">
              <p>
                <Text t="cardType" />:{cardTypeFizikselText && <span> <Text t="physical" /></span>}
                {(selectedOrder.cardTypeId === true || selectedOrder.cardType === 1) && (
                  <span> <Text t="digital" /></span>
                )}
              </p>
              {cardTypeFizikselText && (
                <p>
                  <Text t="cardPrintOptions" />: <span>{cardParemetersText()}</span>
                </p>
              )}
              <p>
                <Text t="invoiceMonth" />: <span>{invoiceMonthText}</span>
              </p>
              {selectedOrder?.eInvoiceInformation !== '' &&
                selectedOrder?.eInvoiceInformation !== undefined && (
                  <p>
                    <Text t="eInvoiceAdditionalInfo" />:{' '}
                    <span>{selectedOrder?.eInvoiceInformation}</span>
                  </p>
                )}
            </div>
          )}
          {(isCompliments || isMarket) ? (
            <div className="summary-info-box box-1" style={{ backgroundColor: '#f2f4f5' }}>
              <p>
                <Text t={'numberOfCards3'} />:
              </p>
              <h5 style={{ color: '#f66301' }}>{selectedOrder?.totalCount}</h5>
            </div>
          ) : (
            <div className="summary-info-box box-1" style={{ backgroundColor: '#e9ecef' }}>
              <p>
                <Text t={'totalCards'} />:
              </p>
              <h5 style={{ color: '#f72717' }}>{selectedOrder?.totalCount}</h5>
            </div>
          )}
          {(isCompliments || isMarket) ? (
            <div className="summary-info-box box-2" style={{ backgroundColor: '#f2f4f5' }}>
              <p>
                <Text t={'totalBalanceToBeLoaded2'} />:
              </p>
              <h5 style={{ color: '#f66301' }}>{moneyFormat(selectedOrder?.totalPrice, true)}</h5>
            </div>
          ) : (
            <div className="summary-info-box box-2" style={{ backgroundColor: '#e9ecef' }}>
              <p>
                <Text t={'totalBalanceLoad'} />:
              </p>
              <h5 style={{ color: '#f72717' }}>{moneyFormat(selectedOrder?.totalPrice, true)}</h5>
            </div>
          )}
        </div>
        {(isCompliments || isMarket) ? (
          <div
            className="summary-info"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 20,
              marginTop: 20,
              marginBottom: 10,
            }}
          >
            <p>
              <div className="gift-summary-text">
                <Text t="cardType2" />:<span> {selectedOrder.cardTypeName}</span>
              </div>
            </p>
            {cardTypeFizikselText && (
              <p>
                <div className="gift-summary-text">
                  <Text t="cardPrintOptions" />: <span>{cardParemetersText()}</span>
                </div>
              </p>
            )}
            <p>
              <div className="gift-summary-text">
                <Text t="invoiceMonth" />: <span>{invoiceMonthText}</span>
              </div>
            </p>
            {selectedOrder?.eInvoiceInformation !== '' &&
              selectedOrder?.eInvoiceInformation !== undefined && (
                <p>
                  <div className="gift-summary-text">
                    <Text t="eInvoiceAdditionalInfo" />:{' '}
                    <span>{selectedOrder?.eInvoiceInformation}</span>
                  </div>
                </p>
              )}
          </div>
        ) : null}
        <div className="summary-list-content">
          <CustomTable
            className="card-list-table"
            pagination={false}
            dataSource={data.list}
            columns={columns}
            rowKey="uid"
          />
        </div>
        <div
          className="order-summary-table-footer"
          style={{ justifyContent: (isCompliments || isMarket) ? 'center' : 'space-between' }}
        >
          <div className="d-flex">
            <CustomPagination
              current={data?.tableProperty?.currentPage}
              defaultCurrent={1}
              pageSize={data?.tableProperty?.pageSize}
              total={data?.tableProperty?.totalCount}
              onChange={async (pageNumber, pageSize) => {
                await handleTableChange(pageNumber, pageSize);
              }}
            />
          </div>
          {(isCompliments || isMarket) ? null : (
            <div className="footer-info-container">
              <div className="footer-info-content">
                <CustomImage src={cardIcon} />
                <Text t="total" /> <span>{selectedOrder?.totalCount}</span> <Text t={cardText} />
              </div>
              <div className="total-amount-title">
                <Text t="totalBalanceLoad" />:
                <span>{moneyFormat(selectedOrder?.totalPrice, true)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </CustomModal>
  );
};
OrderSummaryCardModal.propTypes = {
  modalVisible: PropTypes.any,
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
  handleModalVisible: PropTypes.func,
  isPending: PropTypes.bool,
  fromPage: PropTypes.bool,
};
export default OrderSummaryCardModal;
